import React, { useState } from 'react';
import axios from 'axios';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../AuthProvider';
import { Button, Spinner } from '../ui';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export default function ChangePasswordModal() {
  const { user, setUser, setToken, clearMustChangePassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user?.must_change_password) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const email = (user?.email || '').trim();
    if (!email) {
      setError('Session missing email. Please log in again.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      // Verify new password works by signing in (Supabase may invalidate session after password change).
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password: newPassword });
      if (signInError) {
        throw new Error('Password was updated but could not sign in with it. Use the new password on the login page, or ask an admin to reset your password.');
      }
      if (signInData?.session?.access_token) {
        const response = await axios.post(`${API_BASE}/auth/supabase`, new URLSearchParams({ token: signInData.session.access_token }), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
        const { access_token, user: backendUser } = response.data || {};
        if (access_token && backendUser) {
          setUser({ ...backendUser, must_change_password: false });
          setToken(access_token);
        }
      }
      await clearMustChangePassword();
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-1">Set your password</h2>
        <p className="text-sm text-gray-500 dark:text-neutral-400 mb-4">
          You signed in with a temporary password. Please set a new password to continue.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">New password</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100"
              placeholder="At least 8 characters"
              minLength={8}
              disabled={submitting}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100"
              placeholder="Confirm new password"
              disabled={submitting}
              autoComplete="new-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : 'Set password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
